import { ReactNode } from 'react';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    IconButton,
    Typography,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useTranslation } from 'react-i18next';

// Estilos convertidos a objetos constantes para usar con sx
export const contactStyles = {
    pageBg: {
        background:
            'linear-gradient(180deg, rgba(248,250,252,1) 0%, rgba(241,245,249,1) 50%, rgba(248,250,252,1) 100%)',
        borderRadius: 3,
        padding: { xs: 2, sm: 4 },
        paddingBottom: { xs: 3, sm: 6 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '80%',
            height: '1px',
            background:
                'linear-gradient(90deg, transparent 0%, rgba(186, 242, 0, 0.3) 50%, transparent 100%)',
        },
    },

    back: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        color: '#64748B',
        '&:hover': { color: '#0F172A' },
    },
    backWrap: { marginBottom: 2 },
    hero: {
        marginTop: 2,
        marginBottom: 5,
        textAlign: 'center',
    },
    heroTitle: {
        fontWeight: 900,
        fontSize: { xs: '2.2rem', md: '2.8rem' },
        lineHeight: 1.15,
        color: '#0f172a',
        letterSpacing: '-0.5px',
    },
    heroSub: {
        color: '#64748b',
        maxWidth: 600,
        margin: '16px auto 0',
        fontSize: '1.1rem',
        lineHeight: 1.6,
    },

    gridWrap: { maxWidth: 1080, margin: '0 auto' },

    card: {
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2.5,
        border: '1px solid rgba(15, 23, 42, 0.06)',
        boxShadow: '0 4px 24px rgba(2, 6, 23, 0.08)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)',
        transition:
            'transform .3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow .3s ease',
        '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 20px 50px rgba(2, 6, 23, 0.15)',
        },
    },
    accent: {
        position: 'absolute',
        inset: 0,
        opacity: 0,
        transition: 'opacity .25s ease',
        pointerEvents: 'none',
    },

    badge: {
        display: 'inline-flex',
        padding: { xs: 1.5, sm: 2 },
        paddingX: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        marginBottom: 3,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.25,
        '& svg': {
            fontSize: '1.8rem',
        },
    },
    badgeBlue: { background: 'rgba(59,130,246,0.12)', color: '#2563EB' },
    badgeSky: { background: 'rgba(14,165,233,0.12)', color: '#0EA5E9' },
    badgeGreen: { background: 'rgba(16,185,129,0.12)', color: '#059669' },
    badgeWa: { background: 'rgba(34,197,94,0.12)', color: '#16A34A' },
    badgeUser: { background: 'rgba(59,130,246,0.12)', color: '#2563EB' },
    badgeAdmin: { background: 'rgba(16,185,129,0.12)', color: '#059669' },
    badgeNew: { background: 'rgba(251,146,60,0.16)', color: '#EA580C' },

    title: {
        fontWeight: 800,
        marginBottom: 1,
        fontSize: '1.25rem',
        color: '#1e293b',
    },
    subtitle: {
        color: '#64748B',
        lineHeight: 1.6,
        fontSize: '0.95rem',
    },

    pill: {
        background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
        borderRadius: 1.5,
        padding: { xs: 1, sm: 1.5 },
        paddingX: { xs: 2, sm: 2.5 },
        textAlign: 'center',
        fontWeight: 700,
        color: '#334155',
        fontSize: '1.05rem',
        letterSpacing: '0.3px',
    },

    btn: {
        width: '100%',
        borderRadius: 1.75,
        padding: { xs: 1.25, sm: 1.5 },
        paddingX: { xs: 2.5, sm: 3 },
        textTransform: 'none',
        fontWeight: 700,
        fontSize: '0.95rem',
        color: '#fff !important',
        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
        transition: 'transform .2s ease, box-shadow .2s ease, filter .2s ease',
        '&.MuiButton-contained': { color: '#fff' },
        '&.MuiButton-containedPrimary': { color: '#fff' },
        '& .MuiButton-label': { color: 'inherit', fontWeight: 700 },
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
        },
    },
    btnBlue: {
        background: 'linear-gradient(135deg,#2563EB 0%,#1D4ED8 100%)',
        '&:hover': { filter: 'brightness(1.08)' },
    },
    btnSky: {
        background: 'linear-gradient(135deg,#0EA5E9 0%,#06B6D4 100%)',
        '&:hover': { filter: 'brightness(1.08)' },
    },
    btnGreen: {
        background: 'linear-gradient(135deg,#22C55E 0%,#16A34A 100%)',
        '&:hover': { filter: 'brightness(1.08)' },
    },
    btnOrange: {
        background: 'linear-gradient(135deg,#F59E0B 0%,#F97316 100%)',
        '&:hover': { filter: 'brightness(1.08)' },
    },

    infoBox: {
        marginTop: 6,
        borderRadius: 2.5,
        border: '1px solid #fde68a',
        background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
        padding: { xs: 2.5, sm: 3 },
        paddingX: { xs: 3, sm: 4 },
        maxWidth: 700,
        margin: '48px auto 0',
        boxShadow: '0 4px 20px rgba(217, 119, 6, 0.08)',
    },
    infoIcon: {
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        color: '#d97706',
        borderRadius: '50%',
        width: 48,
        height: 48,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 2.5,
        boxShadow: '0 2px 8px rgba(217, 119, 6, 0.15)',
    },
};

// Helper function to merge badge styles
export const getBadgeStyles = (badgeClass: keyof typeof contactStyles) => {
    return { ...contactStyles.badge, ...contactStyles[badgeClass] };
};

// Helper function to merge button styles
export const getButtonStyles = (btnClass: keyof typeof contactStyles) => {
    return { ...contactStyles.btn, ...contactStyles[btnClass] };
};

// ================== helpers ==================
export const buildWaLink = (rawNumber: string, text?: string): string => {
    const digits = String(rawNumber || '').replace(/[^\d]/g, '');
    return `https://wa.me/${digits}?text=${encodeURIComponent(text || '')}`;
};

export const buildTmeLink = (handleOrUrl: string): string => {
    if (!handleOrUrl) return '';
    if (handleOrUrl.startsWith('http')) return handleOrUrl;
    const clean = handleOrUrl.replace(/^@/, '');
    return `https://t.me/${clean}`;
};

// ================== primitives ==================
type BackButtonProps = {
    onBack: () => void;
};

export function BackButton({ onBack }: BackButtonProps) {
    const { t } = useTranslation();
    return (
        <Box sx={contactStyles.backWrap}>
            <IconButton
                sx={contactStyles.back}
                onClick={onBack}
                size="small"
                title={t('Back')}
            >
                <ArrowBackIosIcon fontSize="small" />
                <Typography variant="body2">{t('Back')}</Typography>
            </IconButton>
        </Box>
    );
}

type HeroProps = {
    title: string;
    subtitle?: string;
};

export function Hero({ title, subtitle }: HeroProps) {
    return (
        <Box sx={contactStyles.hero}>
            <Typography sx={contactStyles.heroTitle} component="h1">
                {title}
            </Typography>
            {subtitle && (
                <Typography sx={contactStyles.heroSub} variant="body1">
                    {subtitle}
                </Typography>
            )}
        </Box>
    );
}

type InfoBoxProps = {
    icon: ReactNode;
    title: string;
    text: string;
};

export function InfoBox({ icon, title, text }: InfoBoxProps) {
    return (
        <Box sx={contactStyles.infoBox}>
            <Box display="flex" alignItems="center" flexWrap="wrap">
                <Box sx={contactStyles.infoIcon}>{icon}</Box>
                <div>
                    <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                        {title}
                    </Typography>
                    <Typography variant="body2" style={{ color: '#64748B' }}>
                        {text}
                    </Typography>
                </div>
            </Box>
        </Box>
    );
}

// Tarjeta para la pantalla de selección (Pick)
type OptionCardProps = {
    icon: ReactNode;
    badgeClass: keyof typeof contactStyles;
    title: string;
    subtitle: string;
    buttonClass: keyof typeof contactStyles;
    onClick: () => void;
    buttonText: string;
};

export function OptionCard({
    icon,
    badgeClass,
    title,
    subtitle,
    buttonClass,
    onClick,
    buttonText,
}: OptionCardProps) {
    return (
        <Card sx={contactStyles.card} variant="outlined">
            <CardContent>
                <Box sx={getBadgeStyles(badgeClass)}>{icon}</Box>
                <Typography variant="h6" sx={contactStyles.title}>
                    {title}
                </Typography>
                <Typography variant="body1" sx={contactStyles.subtitle}>
                    {subtitle}
                </Typography>
            </CardContent>
            <CardActions style={{ padding: 16, marginTop: 'auto' }}>
                <Button
                    fullWidth
                    sx={getButtonStyles(buttonClass)}
                    onClick={onClick}
                >
                    {buttonText}
                </Button>
            </CardActions>
        </Card>
    );
}

// Type for contact methods used across components
export type ContactMethod = {
    key?: string;
    icon: ReactNode;
    title: string;
    description: string;
    contact?: string;
    href: string;
    badgeClass: keyof typeof contactStyles;
    btnClass: keyof typeof contactStyles;
    accent: string;
    buttonText: string;
    showButton?: boolean;
};

// Tarjeta para métodos de contacto (User/Admin/New)
export function MethodCard({
    icon,
    title,
    description,
    contact,
    href,
    badgeClass,
    btnClass,
    accent,
    buttonText,
    showButton = true,
}: ContactMethod) {
    const isExternal = href?.startsWith('http');

    return (
        <Card
            sx={{
                ...contactStyles.card,
                '&:hover .accent': { opacity: 0.06 },
            }}
            variant="outlined"
        >
            <Box
                className="accent"
                sx={{ ...contactStyles.accent, background: accent }}
            />
            <CardContent style={{ paddingBottom: showButton ? undefined : 16 }}>
                <Box sx={getBadgeStyles(badgeClass)}>{icon}</Box>
                <Typography variant="h6" sx={contactStyles.title}>
                    {title}
                </Typography>
                <Typography variant="body1" sx={contactStyles.subtitle}>
                    {description}
                </Typography>
                {contact && (
                    <Box mt={3} sx={contactStyles.pill}>
                        {contact}
                    </Box>
                )}
            </CardContent>
            {showButton && (
                <CardActions style={{ padding: 16, paddingTop: 0 }}>
                    <Button
                        sx={getButtonStyles(btnClass)}
                        href={href}
                        target={isExternal ? '_blank' : undefined}
                        rel={isExternal ? 'noopener noreferrer' : undefined}
                    >
                        {buttonText}
                    </Button>
                </CardActions>
            )}
        </Card>
    );
}
